/**
 * FEAT-1409 spine core — MCP rendering of the channel-agnostic session-start notice contract.
 * Convex never renders a notice; this file owns the ONLY MCP-side mapping from a notice's
 * neutral `action.verb` to the existing `NextAction` shape, emitted through the existing `next`
 * field (HARD CONSTRAINT 2 — no surface syntax in Convex, no parallel action list).
 */
import type { NextAction } from "../envelope.js";

/**
 * Review finding: question/outcome text is workspace-controlled (decline/force-close reasons,
 * role/person/dependent-entry names) and reaches this renderer's output unsanitized — an MCP
 * client that prints the text response to a terminal (many do) inherits the same ANSI/control-
 * char injection risk BET-269 C3 already guards on the CLI side. This package cannot import
 * packages/cli/src/lib/strip.ts at runtime (mcp-server's isolated Docker build resolves no
 * private workspace package, per this file's own header) — same MCP-twin duplication convention
 * as every other function here, mirroring stripControlChars's own regex verbatim.
 */
// eslint-disable-next-line no-control-regex
const ANSI_AND_CONTROL_RE = /\x1b\[[0-9;]*[A-Za-z]|\x1b\][^\x07]*\x07|\x1b[^[\]0-9;A-Za-z]?|[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g;
function safe(str: string | undefined): string {
  return str ? str.replace(ANSI_AND_CONTROL_RE, "") : "";
}

/**
 * WP-584 S4 (FEAT-1412): the always-on feedback-invite line — OBJ-85's baseline "exactly 1
 * line" every session-start response carries regardless of notices. Single source WITHIN this
 * package now (start_pb.ts and session.ts both import it — they used to each carry their own
 * literal, drifted apart in nothing but coincidence). NOT collapsed with packages/cli's own copy
 * (formatters/session.ts's `FEEDBACK_SUBMIT_HINT`): command syntax differs per surface
 * (`feedback action=submit` here vs `pb feedback submit -c <bug|friction|idea|praise|other>
 * "<message>"` there) and mcp-server's isolated Docker build cannot resolve a private workspace
 * package at runtime (packages/kernel-client's own package.json documents this exact
 * constraint — mcp-server only gets a TYPE-ONLY tsconfig path alias to it, never a runtime
 * value import). scripts/check-notice-hint-drift.mjs is the drift guard for the SHARED
 * substance between the two packages' copies.
 */
export const FEEDBACK_SUBMIT_HINT =
  "Friction or ideas? Use `feedback action=submit` — describe how pb behaved, not your workspace content. Once screening completes, it enters the builders' triage queue.";

export interface AgentSessionStartNoticeAction {
  verb: string;
  args: Record<string, string | number | boolean | null>;
  /** Optional, additive — the original §3.9a verb label (convex/lib/notices/types.ts's
   *  NoticeAction.label doc comment: the route alone can't distinguish 'take it' from
   *  'reassign', or 'pass' from 'decline'). */
  label?: string;
}

/** WP-638 S4 (§7.5) — the MCP-side twin of convex/lib/notices/types.ts's `NoticeQuestionBrief`. */
export interface AgentSessionStartQuestionBrief {
  name: string;
  question: string;
  asker: string;
  sourcePointer: string;
  domain: string;
  whyYou: string;
  ageDays: number;
  dependents: { count: number; truncated?: boolean; named: { name: string; id: string }[] };
  declineContext?: string;
  answerContext?: string;
  actions: AgentSessionStartNoticeAction[];
}

/** FEAT-1409 spine core — the channel-agnostic session-start notice (convex/lib/notices/types.ts's
 *  `Notice`, mirrored here for the MCP surface). Canonical home for the MCP-side notice shape —
 *  client.ts re-exports it (STD-2/DEC-1504: client.ts is grandfathered shrink-only). */
export interface AgentSessionStartNotice {
  source: string;
  type: string;
  severity: "none" | "advisory" | "warning" | "emergency";
  subject: string;
  change: string;
  occurredAt: number;
  action: AgentSessionStartNoticeAction;
  /** WP-638 S4 (§7.5, R23) — the Notice-contract extension, additive/optional. */
  payload?: { questions: AgentSessionStartQuestionBrief[]; truncated?: boolean };
  requiresDisposition?: boolean;
  actions?: AgentSessionStartNoticeAction[];
}

/**
 * verb -> the NextAction this MCP surface already exposes for it. `feedback.list` (S1,
 * feedback-status) and `feedback.queue` (S4/TEN-2971, feedback-queue — the vendor triage
 * standing notice) both map to the existing `feedback` compound tool's own actions — HARD
 * CONSTRAINT 10, no new gateway route / no new tool.
 *
 * Returns null for an unmapped verb (review round 1, Copilot). The previous fallback emitted
 * `{ tool: verb }`, which is NOT a registered MCP tool name (see src/server.ts) — so a future
 * producer's verb would have handed the agent a structured action that fails on invocation. A
 * `NextAction` is machine-consumable, unlike the CLI's verb fallback, which is only ever
 * rendered as display text (packages/cli/src/lib/sessionNotices.ts) and stays honest as-is.
 *
 * Dropping the ACTION is not dropping the NOTICE: `formatNoticeLines` still renders the
 * notice's subject and change, so an unmapped verb degrades to "you are told about it, with no
 * one-click action" rather than to a broken tool call.
 */
// WP-638 S4 (R24 — moved here from S5 so the mapping ships in the slice whose doneWhen quotes
// it, §16.2 S4(j)): the eight routed-question verbs join feedback's two.
function nextActionForVerb(verb: string, args: Record<string, string | number | boolean | null>): NextAction | null {
  switch (verb) {
    case "feedback.list":
      return { tool: "feedback", description: "Read your workspace's own feedback (all statuses)", parameters: { action: "list" } };
    case "feedback.queue":
      return { tool: "feedback", description: "Read the vendor triage queue (system admins only)", parameters: { action: "queue" } };
    case "question.create":
      return { tool: "question", description: "Create a routed question", parameters: { action: "create" } };
    case "question.adopt":
      return { tool: "question", description: "Attach routing to a captured tension", parameters: { action: "adopt" } };
    case "question.assign":
      return { tool: "question", description: "Confirm or reassign a question", parameters: { action: "assign" } };
    case "question.snooze":
      return { tool: "question", description: "Snooze a routed/escalated question", parameters: { action: "snooze" } };
    case "question.decline":
      return { tool: "question", description: "Pass on or decline a question", parameters: { action: "decline" } };
    case "question.answer":
      return { tool: "question", description: "Answer a question", parameters: { action: "answer" } };
    case "question.forceClose":
      return { tool: "question", description: "Team-Lead-only force-close", parameters: { action: "force-close" } };
    case "question.list":
      // Review finding: the outcomes producer's asker-arm action carries `{ mine: true }`
      // (§7.3(a)) so the reader lands on their OWN history, not the ack-gate obligation view
      // (which excludes exactly the processed question the notice is about).
      return { tool: "question", description: args.mine === true ? "List every question you asked" : "List open questions", parameters: { action: "list", ...(args.mine === true ? { mine: true } : {}) } };
    default:
      return null;
  }
}

export function noticesToNextActions(notices: readonly AgentSessionStartNotice[] | undefined): NextAction[] {
  if (!notices || notices.length === 0) return [];
  const actions = notices
    .map((n) => nextActionForVerb(n.action.verb, n.action.args))
    .filter((a): a is NextAction => a !== null);
  // Review finding: a session with many outcome notices (each carrying the identical
  // `question action=list mine=true/false` next-action, §7.3) produced one indistinguishable
  // duplicate per outcome — dedupe by tool+parameters, keeping the first (earliest) description.
  const seen = new Set<string>();
  return actions.filter((a) => {
    const key = `${a.tool}:${JSON.stringify(a.parameters)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const QUESTION_ACK_GATE_SOURCE_ID = "question-ack-gate";

/**
 * §7.5's verbatim block, the MCP twin of packages/cli/src/lib/sessionNotices.ts's
 * `formatQuestionAckGateBlock` — same fixed elements, same canonical closing sentence, quoted
 * identically (§7.5/§10/§16.2 S4(f)).
 */
/** display verb (e.g. "take it") from the neutral gateway route name a brief's own `actions`
 *  carry — the inverse of convex/lib/questionDisplayStrings.ts's `questionVerbToRoute`. */
function displayVerbFromRoute(route: string): string {
  switch (route) {
    case "question.assign": return "take it / reassign";
    case "question.decline": return "pass / decline";
    case "question.forceClose": return "force-close";
    default: return route.replace(/^question\./, "");
  }
}

function formatQuestionBrief(q: AgentSessionStartQuestionBrief): string[] {
  const lines: string[] = [];
  lines.push(`  [${safe(q.sourcePointer)}] ${safe(q.name)} — ${safe(q.whyYou)}, ${q.ageDays}d`);
  lines.push(`    "${safe(q.question)}" — asked by ${safe(q.asker)}${q.domain ? `, ${safe(q.domain)} domain` : ""}`);
  if (q.dependents.count > 0) {
    const names = q.dependents.named.map((d) => `${safe(d.name)} (${safe(d.id)})`).join(", ");
    const countLabel = q.dependents.truncated ? `${q.dependents.count}+` : String(q.dependents.count);
    lines.push(`    blocks ${countLabel} item(s)${names ? `: ${names}` : ""}`);
  }
  if (q.declineContext) lines.push(`    declined: ${safe(q.declineContext)}`);
  if (q.answerContext) lines.push(`    ${safe(q.answerContext)}`);
  // Prefer the server's own `label` (the actual §3.9a verb legal for this row) over the
  // route-derived guess, which collapses 'take it'/'reassign' and 'pass'/'decline' together.
  const verbList = [...new Set(q.actions.map((a) => safe(a.label) || displayVerbFromRoute(a.verb)))].join(" · ") || "answer";
  lines.push(`    ${verbList}  ->  question action=answer entryId=${safe(q.sourcePointer)} text="<your answer>"`);
  return lines;
}

function formatQuestionAckGateBlock(notice: AgentSessionStartNotice): string[] {
  const questions = notice.payload?.questions ?? [];
  const lines: string[] = [];
  lines.push(`- ${questions.length} open question${questions.length === 1 ? "" : "s"} need you before you continue.`);
  for (const q of questions) lines.push(...formatQuestionBrief(q));
  lines.push("  Deal with each question above before proceeding.");
  if (notice.payload?.truncated) {
    lines.push("  (This list may be incomplete — you may have more open questions than shown.)");
  }
  return lines;
}

/** One line per notice, oldest first (server already sorts producers' output this way) — except
 *  the question-ack-gate notice, which renders §7.5's full multi-line block (see the CLI twin's
 *  own doc comment for why this is additive and every other producer's one-liner is untouched). */
export function formatNoticeLines(notices: readonly AgentSessionStartNotice[] | undefined): string[] {
  if (!notices || notices.length === 0) return [];
  return notices.flatMap((n) =>
    n.source === QUESTION_ACK_GATE_SOURCE_ID && n.payload
      ? formatQuestionAckGateBlock(n)
      : [`- ${safe(n.subject)} (${safe(n.change)})`],
  );
}

/** WP-638 S4 (§10.3) — the bounded adopt-discovery advisory line, MCP twin of the CLI's own
 *  `formatAdoptDiscoveryLine`. `display` is the server's own pre-formatted count ("3" or, at the
 *  scan cap, "20+") — silence when absent. */
export function formatAdoptDiscoveryLine(display: string | undefined): string[] {
  if (!display) return [];
  return [`- ${display} open question${display === "1" ? "" : "s"} you raised have no owner yet — use \`question action=adopt\` to route one.`];
}
